import { SelectionActions } from './SelectionActions';
import { useTranslation } from 'react-i18next';
import { hasSelection } from '@peruselab/structure-editor/domain/editor';
import { EDITOR_ELEMENTS } from '@peruselab/structure-editor/chemistry/elements';
import { Icon } from '../Icon';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import type { usePartialCharges } from '../../hooks/usePartialCharges';

interface EditorToolPaletteProps {
  readonly doc: UseDrawingDocumentResult;
  readonly partialCharges: ReturnType<typeof usePartialCharges>;
}

export function EditorToolPalette({ doc, partialCharges }: EditorToolPaletteProps) {
  const { t } = useTranslation();
  const {
    tool,
    selection,
    document,
    selectedAtom,
    updateSelectedAtom,
    selectedFormalCharge,
    updateSelectedFormalCharge,
    updateSelectedIsotope,
    updateSelectedRadicalElectronCount,
    selectedAtomStereo,
    singleAtomId
  } = doc;
  const selectedPartialCharge = singleAtomId ? partialCharges.result?.charges[singleAtomId] : undefined;
  return (
    <section className="editor-tools" aria-label={t('editorToolPalette.ariaLabel')}>
      <div className="property-palette">
        {/* #840 (owner): no per-tool hint lines here -- the toolbar's and rail's tooltips name
            the tools. The selection summary stays. */}
        {tool === 'select' && hasSelection(selection) ? (
          <>
            <strong>
              {t('editorToolPalette.selection.atomCount', { count: selection.atomIds.length })}
              {', '}
              {t('editorToolPalette.selection.bondCount', { count: selection.bondIds.length })}{' '}
              {t('editorToolPalette.selection.selected')}
            </strong>
          </>
        ) : null}
        <SelectionActions doc={doc} />
        {document.structure.graph.nodes.length > 0 ? (
          <div className="partial-charge-control">
            <button
              type="button"
              className="icon-button partial-charge-button"
              disabled={partialCharges.state.state === 'loading'}
              onClick={() => void partialCharges.runCompute()}
              aria-label={
                partialCharges.state.state === 'loading'
                  ? t('editorToolPalette.partialCharge.computing')
                  : t('editorToolPalette.partialCharge.compute')
              }
              title={
                partialCharges.state.state === 'loading'
                  ? t('editorToolPalette.partialCharge.computing')
                  : t('editorToolPalette.partialCharge.compute')
              }
            >
              {/* Text as glyph, like the R-group labels: δ± is chemistry's own mark for a partial charge. */}
              <span className="partial-charge-glyph" aria-hidden="true">
                δ±
              </span>
            </button>
            {partialCharges.state.state === 'error' ? (
              <span className="partial-charge-error" role="alert">
                {partialCharges.state.message}
              </span>
            ) : null}
          </div>
        ) : null}
        {selectedAtom ? (
          <>
            <strong>{t('editorToolPalette.atom.elementHeading')}</strong>
            <div className="rail-element-grid">
              {EDITOR_ELEMENTS.map((element) => (
                <button
                  key={element.atomicNumber}
                  type="button"
                  title={element.name}
                  className={selectedAtom.atomicNumber === element.atomicNumber ? 'active' : ''}
                  aria-pressed={selectedAtom.atomicNumber === element.atomicNumber}
                  onClick={() => updateSelectedAtom(element.atomicNumber)}
                >
                  {element.symbol}
                </button>
              ))}
            </div>
            <label className="formal-charge-editor">
              <span>{t('editorToolPalette.atom.formalCharge')}</span>
              <input
                type="number"
                step="1"
                value={selectedFormalCharge?.formalCharge ?? 0}
                onChange={(event) => updateSelectedFormalCharge(event.currentTarget.valueAsNumber)}
              />
            </label>
            <label className="isotope-editor">
              <span>{t('editorToolPalette.atom.isotopeMassNumber')}</span>
              <input
                type="number"
                step="1"
                min="1"
                max="300"
                placeholder={t('editorToolPalette.atom.isotopeStandard')}
                value={selectedAtom.isotope ?? ''}
                onChange={(event) => {
                  const value = event.currentTarget.valueAsNumber;
                  updateSelectedIsotope(Number.isNaN(value) ? null : value);
                }}
              />
            </label>
            <label className="radical-editor">
              <span>{t('editorToolPalette.atom.radicalElectrons')}</span>
              <select
                value={selectedAtom.radicalElectronCount ?? 0}
                onChange={(event) => {
                  const value = Number(event.currentTarget.value);
                  updateSelectedRadicalElectronCount(value === 0 ? null : value);
                }}
              >
                <option value={0}>{t('editorToolPalette.atom.radicalNone')}</option>
                <option value={1}>{t('editorToolPalette.atom.radicalDoublet')}</option>
                <option value={2}>{t('editorToolPalette.atom.radicalTriplet')}</option>
                <option value={3}>{t('editorToolPalette.atom.radicalThree')}</option>
              </select>
            </label>
            {selectedAtomStereo ? (
              <span>
                {t('editorToolPalette.atom.perceivedConfiguration', {
                  value: selectedAtomStereo.descriptor ?? selectedAtomStereo.parity
                })}
              </span>
            ) : null}
            {selectedPartialCharge !== undefined ? (
              <span>{t('editorToolPalette.partialCharge.atomLabel', { value: selectedPartialCharge.toFixed(3) })}</span>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
